import { overtimePay } from "../utils/monthlySalary";
import { safeParseDate, getMonthIndexFromSwedish, pad, formatDateKey, calculateShiftHours, calculateOvertimeForShift, calculateAbsenceForShift, calculateEntryHours, getFortnoxAbsenceCode, calculateObForShift, mergeApprovedOvertimeForEmployee, mergeApprovedAbsenceForEmployee } from "../utils/payrollCalculations";
// src/pages/ExportPayrollPage.js
// Uppdaterad: OB-beräkning räknar varje OB-källa separat (samma som TimeReportPopup)
// Uppdaterad: godkända övertidsansökningar slås ihop med manuell övertid och visas i ÖT-pengar
import React, { useEffect, useMemo, useState } from "react";
import LoadingIndicator from "../components/LoadingIndicator";
import { calculateAbsencePay } from "../utils/absencePay";
import { FaSearch, FaFileExport } from "react-icons/fa";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  where,
  writeBatch,
} from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useCompany } from "../contexts/CompanyContext";
import ExportSystemPopup from "../popup/ExportSystemPopup";
import TimeReportPopup from "../popup/TimeReportPopup";

// ===== HJÄLPFUNKTIONER =====

const formatHours = (hours) =>
  Number(hours || 0).toLocaleString("sv-SE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatCurrency = (amount) =>
  `${Number(amount || 0).toLocaleString("sv-SE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} kr`;

const calculateNetSalary = (gross, tax) => {
  const parsedTax = Number(tax || 0);
  const taxRate = Number.isFinite(parsedTax) ? Math.max(0, Math.min(100, parsedTax)) : 0;
  return Number(gross || 0) * (1 - taxRate / 100);
};

const VACATION_PAY_PERCENT = 12;

// ===== HUVUDKOMPONENT =====

export default function ExportPayrollPage() {
  const { currentUser, profile } = useAuth();
  const { activeCompany, loading: companyLoading } = useCompany();

  const [companyRootUid, setCompanyRootUid] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [employeeGroups, setEmployeeGroups] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [obRules, setObRules] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [overtimeRules, setOvertimeRules] = useState([]);
  const [approvedOvertimeRequests, setApprovedOvertimeRequests] = useState([]);
  const [absenceTypes, setAbsenceTypes] = useState([]);
  const [approvedAbsenceRequests, setApprovedAbsenceRequests] = useState([]);
  const [customObEntries, setCustomObEntries] = useState([]);

  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [selectedApprovalStatus, setSelectedApprovalStatus] = useState("all");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [showExportPopup, setShowExportPopup] = useState(false);
  const [selectedPayrollEmployeeId, setSelectedPayrollEmployeeId] = useState(null);
  const [payrollApprovals, setPayrollApprovals] = useState({ companyRootUid: null, period: "", records: new Map(), ready: false });
  const [approvalSaving, setApprovalSaving] = useState(false);
  const [approvalError, setApprovalError] = useState("");

  const monthsInSwedish = [
    "Januari",
    "Februari",
    "Mars",
    "April",
    "Maj",
    "Juni",
    "Juli",
    "Augusti",
    "September",
    "Oktober",
    "November",
    "December",
  ];

  const years = useMemo(() => {
    const currentYearNum = new Date().getFullYear();
    const list = [];

    for (let year = 2020; year <= currentYearNum + 2; year++) {
      list.push(year.toString());
    }

    return list;
  }, []);

  const styles = {
    page: {
      minHeight: "100vh",
      background: "#f6f7fb",
      padding: "22px 24px 40px",
      fontFamily: "Inter, sans-serif",
      color: "#1f2937",
      boxSizing: "border-box",
    },
    container: {
      width: "100%",
      maxWidth: "1400px",
      margin: "0 auto",
    },
    topRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      gap: "16px",
      flexWrap: "wrap",
      marginBottom: "14px",
    },
    title: {
      margin: 0,
      fontSize: "42px",
      lineHeight: 1.1,
      fontWeight: 800,
      color: "#101b3a",
      letterSpacing: "-1px",
    },
    subtitle: {
      margin: "12px 0 0",
      fontSize: "15px",
      lineHeight: 1.45,
      color: "#5f6b85",
      maxWidth: "640px",
    },
    exportButton: {
      border: "none",
      background: "#2f63e0",
      color: "#fff",
      borderRadius: "999px",
      padding: "10px 20px",
      minWidth: "160px",
      fontSize: "16px",
      fontWeight: 800,
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      justifyContent: "center",
    },
    divider: {
      height: 1,
      background: "#dde3ef",
      margin: "16px 0 18px",
    },
    summaryPanel: {
      background: "#edf3ff",
      borderRadius: "14px",
      padding: "20px",
      marginBottom: "24px",
      border: "1px solid #e0e8fb",
    },
    summaryTitle: {
      margin: 0,
      fontSize: "20px",
      fontWeight: 700,
      color: "#2552d8",
    },
    summaryMeta: {
      margin: "6px 0 16px",
      fontSize: "14px",
      fontWeight: 600,
      color: "#2552d8",
    },
    summaryGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
      gap: "16px",
    },
    summaryCard: {
      background: "#fff",
      border: "1px solid #d8deea",
      borderRadius: "14px",
      padding: "18px 16px",
      display: "flex",
      flexDirection: "column",
      gap: "8px",
    },
    summaryCardLabel: {
      fontSize: "13px",
      fontWeight: 600,
      color: "#6b7280",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
    },
    summaryCardValue: {
      fontSize: "28px",
      fontWeight: 800,
      color: "#111827",
    },
    filtersWrap: {
      display: "grid",
      gridTemplateColumns: "minmax(180px, 1fr) repeat(2, minmax(145px, 200px)) repeat(2, minmax(120px, 148px))",
      gap: "10px",
      background: "#fff",
      borderRadius: "12px",
      padding: "12px",
      marginBottom: "20px",
      border: "1px solid #e5e9f0",
    },
    searchWrap: {
      position: "relative",
    },
    searchIcon: {
      position: "absolute",
      left: "14px",
      top: "50%",
      transform: "translateY(-50%)",
      color: "#9ca3af",
      fontSize: "14px",
    },
    searchInput: {
      width: "100%",
      height: "42px",
      borderRadius: "10px",
      border: "1px solid #e2e6ed",
      padding: "0 14px 0 40px",
      fontSize: "14px",
      background: "#fff",
      outline: "none",
      boxSizing: "border-box",
    },
    select: {
      width: "100%",
      height: "42px",
      borderRadius: "10px",
      border: "1px solid #e2e6ed",
      padding: "0 12px",
      fontSize: "14px",
      background: "#fff",
      outline: "none",
      cursor: "pointer",
    },
    tableWrap: {
      background: "#fff",
      border: "1px solid #e5e9f0",
      borderRadius: "14px",
      overflow: "hidden",
      boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
    },
    table: {
      width: "100%",
      borderCollapse: "collapse",
      minWidth: "900px",
    },
    thead: {
      background: "#f8fafd",
    },
    th: {
      textAlign: "left",
      fontSize: "13px",
      fontWeight: 700,
      color: "#4a5568",
      padding: "14px 16px",
      borderBottom: "1px solid #e5e9f0",
      whiteSpace: "nowrap",
    },
    td: {
      fontSize: "14px",
      color: "#1a202c",
      padding: "14px 16px",
      borderBottom: "1px solid #edf2f7",
    },
    nameCell: {
      fontWeight: 800,
      color: "#111827",
    },
    totalSalaryCell: {
      fontWeight: 900,
      color: "#111827",
    },
    emptyState: {
      background: "#fff",
      border: "1px solid #e5e9f0",
      borderRadius: "14px",
      padding: "48px",
      textAlign: "center",
      color: "#6b7280",
    },
  };

  // ===== INITIALISERING =====

  useEffect(() => {
    const currentDate = new Date();

    setSelectedMonth(monthsInSwedish[currentDate.getMonth()]);
    setSelectedYear(currentDate.getFullYear().toString());
  }, []);

  useEffect(() => {
    let isMounted = true;

    const resolveCompanyRootUid = async () => {
      setLoading(true);

      if (activeCompany?.companyAccountUid) {
        if (isMounted) setCompanyRootUid(activeCompany.companyAccountUid);
        return;
      }

      if (!currentUser?.uid) {
        if (isMounted) {
          setCompanyRootUid(null);
          setLoading(false);
        }
        return;
      }

      try {
        const currentUserRef = doc(db, "users", currentUser.uid);
        const currentUserSnap = await getDoc(currentUserRef);

        if (!currentUserSnap.exists()) {
          if (isMounted) setCompanyRootUid(currentUser.uid);
          return;
        }

        const currentUserData = currentUserSnap.data();
        const userCompanyId = currentUserData.companyId || null;

        if (!userCompanyId) {
          if (isMounted) setCompanyRootUid(currentUser.uid);
          return;
        }

        const superAdminQuery = query(
          collection(db, "users"),
          where("companyId", "==", userCompanyId),
          where("role", "==", "super-admin"),
          limit(1)
        );

        const superAdminSnap = await getDocs(superAdminQuery);

        if (!superAdminSnap.empty) {
          if (isMounted) setCompanyRootUid(superAdminSnap.docs[0].id);
          return;
        }

        const adminQuery = query(
          collection(db, "users"),
          where("companyId", "==", userCompanyId),
          where("role", "==", "admin"),
          limit(1)
        );

        const adminSnap = await getDocs(adminQuery);

        if (!adminSnap.empty) {
          if (isMounted) setCompanyRootUid(adminSnap.docs[0].id);
          return;
        }

        if (isMounted) setCompanyRootUid(currentUser.uid);
      } catch (error) {
        console.error("Fel vid hämtning av företagets root uid:", error);
        if (isMounted) setCompanyRootUid(currentUser?.uid || null);
      }
    };

    resolveCompanyRootUid();

    return () => {
      isMounted = false;
    };
  }, [activeCompany?.companyAccountUid, currentUser?.uid]);

  // ===== FIRESTORE SUBSCRIPTIONS =====

  useEffect(() => {
    setSelectedGroupId("");
    if (!companyRootUid) {
      setEmployeeGroups([]);
      return;
    }

    return onSnapshot(
      collection(db, "users", companyRootUid, "employeeGroups"),
      (snapshot) => setEmployeeGroups(snapshot.docs
        .map((groupDoc) => ({ id: groupDoc.id, ...groupDoc.data() }))
        .sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), "sv-SE"))),
      (error) => {
        console.error("Fel vid hämtning av personalgrupper:", error);
        setEmployeeGroups([]);
      }
    );
  }, [companyRootUid]);

  useEffect(() => {
    if (!companyRootUid) {
      setCustomObEntries([]);
      return;
    }

    const customObRef = collection(db, "users", companyRootUid, "customObEntries");

    const unsubscribe = onSnapshot(
      customObRef,
      (snapshot) => {
        const entries = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        setCustomObEntries(entries);
      },
      (error) => {
        console.error("Fel vid hämtning av egna OB-tillägg:", error);
        setCustomObEntries([]);
      }
    );

    return () => unsubscribe();
  }, [companyRootUid]);

  useEffect(() => {
    if (!companyRootUid) {
      setEmployees([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    const employedRef = collection(db, "users", companyRootUid, "employed");

    const unsubscribe = onSnapshot(
      employedRef,
      async (snapshot) => {
        const users = [];

        snapshot.forEach((userDoc) => {
          const userData = userDoc.data();
          const payroll = userData.payroll || {};

          let fullName = userData.name || "";
          if (userData.firstName && userData.lastName) {
            fullName = `${userData.firstName} ${userData.lastName}`;
          } else if (userData.firstName) {
            fullName = userData.firstName;
          } else if (userData.lastName) {
            fullName = userData.lastName;
          } else if (userData.displayName) {
            fullName = userData.displayName;
          } else if (userData.userName) {
            fullName = userData.userName;
          }

          const savedPayType = payroll.savedPayType || "hourly";
          const savedPayAmount =
            payroll.savedPayAmount !== undefined && payroll.savedPayAmount !== null
              ? String(payroll.savedPayAmount)
              : "";

          const savedSalaryTypeId = payroll.savedSalaryTypeId || "";
          const savedSalaryTypeName = payroll.savedSalaryTypeName || "";
          const savedTax = String(payroll.tax ?? 0);
          const savedSemProcent = String(payroll.semProcent ?? 100);
          const savedObEnabled = Boolean(payroll.obEnabled ?? true);
          const savedSemEnabled = Boolean(payroll.semEnabled ?? true);

          users.push({
            id: userDoc.id,
            name: fullName || "Användare",
            firstName: userData.firstName || "",
            lastName: userData.lastName || "",
            email: userData.email || "",
            personalNumber:
              userData.personalNumber || userData.ssn || userData.personnummer || "",
            employmentNumber:
              userData.employmentNumber ||
              userData.employeeNumber ||
              userData.anstallningsnummer ||
              payroll.employmentNumber ||
              "",
            groupId: userData.groupId || "",
            role: userData.role || "user",

            savedPayType,
            weeklyHours: payroll.weeklyHours ?? 40,
            monthlyDivisor: payroll.monthlyDivisor ?? 0,
            savedPayAmount,
            savedSalaryTypeId,
            savedSalaryTypeName,
            savedSemProcent,
            savedTax,
            savedObEnabled,
            savedSemEnabled,

            draftPayType: savedPayType,
            draftPayAmount: savedPayAmount,
            draftSalaryTypeId: savedSalaryTypeId,
            draftSalaryTypeName: savedSalaryTypeName,
            semProcent: savedSemProcent,
            tax: savedTax,
            obEnabled: savedObEnabled,
            semEnabled: savedSemEnabled,
          });
        });

        // Företagsägaren sparas i users/{companyRootUid}, inte alltid i employed.
        // Hämta därför även ägarens personuppgifter så att rätt anställningsnummer
        // kopplas till ägaren i löneexporten.
        try {
          const ownerSnapshot = await getDoc(doc(db, "users", companyRootUid));

          if (ownerSnapshot.exists()) {
            const ownerData = ownerSnapshot.data();
            const ownerEmail = String(ownerData.email || "").trim().toLowerCase();
            const ownerIndex = users.findIndex(
              (employee) =>
                employee.id === ownerSnapshot.id ||
                (ownerEmail && employee.email.toLowerCase() === ownerEmail)
            );

            if (ownerData.role === "super-admin") {
              const payroll = ownerData.payroll || {};
              const ownerName =
                ownerData.name ||
                ownerData.displayName ||
                `${ownerData.firstName || ""} ${ownerData.lastName || ""}`.trim() ||
                ownerData.companyName ||
                "Företagsägare";
              const savedPayType = payroll.savedPayType || "hourly";
              const savedPayAmount =
                payroll.savedPayAmount !== undefined && payroll.savedPayAmount !== null
                  ? String(payroll.savedPayAmount)
                  : "";
              const savedSalaryTypeId = payroll.savedSalaryTypeId || "";
              const savedSalaryTypeName = payroll.savedSalaryTypeName || "";
              const savedSemProcent = String(payroll.semProcent ?? 100);
              const savedTax = String(payroll.tax ?? 0);
              const savedObEnabled = Boolean(payroll.obEnabled ?? true);
              const savedSemEnabled = Boolean(payroll.semEnabled ?? true);
              const ownerPersonalNumber =
                ownerData.personalNumber || ownerData.ssn || ownerData.personnummer || "";
              const ownerEmploymentNumber =
                ownerData.employmentNumber ||
                ownerData.employeeNumber ||
                ownerData.anstallningsnummer ||
                payroll.employmentNumber ||
                "";

              if (ownerIndex !== -1) {
                users[ownerIndex] = {
                  ...users[ownerIndex],
                  name: ownerName,
                  email: ownerData.email || users[ownerIndex].email,
                  personalNumber:
                    ownerPersonalNumber || users[ownerIndex].personalNumber,
                  employmentNumber:
                    ownerEmploymentNumber || users[ownerIndex].employmentNumber,
                  groupId: ownerData.groupId || users[ownerIndex].groupId || "",
                  role: "super-admin",
                };
              } else {

                users.push({
                  id: ownerSnapshot.id,
                  name: ownerName,
                  firstName: ownerData.firstName || "",
                  lastName: ownerData.lastName || "",
                  email: ownerData.email || "",
                  personalNumber: ownerPersonalNumber,
                  employmentNumber: ownerEmploymentNumber,
                  groupId: ownerData.groupId || "",
                  role: "super-admin",
                  savedPayType,
                  weeklyHours: payroll.weeklyHours ?? 40,
            monthlyDivisor: payroll.monthlyDivisor ?? 0,
            savedPayAmount,
                  savedSalaryTypeId,
                  savedSalaryTypeName,
                  savedSemProcent,
                  savedTax,
                  savedObEnabled,
                  savedSemEnabled,
                  draftPayType: savedPayType,
                  draftPayAmount: savedPayAmount,
                  draftSalaryTypeId: savedSalaryTypeId,
                  draftSalaryTypeName: savedSalaryTypeName,
                  semProcent: savedSemProcent,
                  tax: savedTax,
                  obEnabled: savedObEnabled,
                  semEnabled: savedSemEnabled,
                });
              }
            }
          }
        } catch (error) {
          console.error("Kunde inte hämta företagsägarens uppgifter till lön:", error);
        }

        users.sort((a, b) => (a.name || "").localeCompare(b.name || "", "sv-SE"));

        setEmployees(users);
        setLoading(false);
      },
      (error) => {
        console.error("Fel vid hämtning av anställda till lön:", error);
        setEmployees([]);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [companyRootUid]);

  useEffect(() => {
    if (!companyRootUid) {
      setShifts([]);
      return;
    }

    const shiftsRef = collection(db, "users", companyRootUid, "shifts");

    const unsubscribe = onSnapshot(
      shiftsRef,
      (snapshot) => {
        const shiftList = [];

        snapshot.forEach((shiftDoc) => {
          shiftList.push({
            id: shiftDoc.id,
            ...shiftDoc.data(),
          });
        });

        setShifts(shiftList);
      },
      (error) => {
        console.error("Fel vid hämtning av pass:", error);
        setShifts([]);
      }
    );

    return () => unsubscribe();
  }, [companyRootUid]);

  useEffect(() => {
    if (!companyRootUid) {
      setBookings([]);
      return;
    }

    const bookingsRef = collection(db, "users", companyRootUid, "bookings");
    const bookingsQuery = query(
      bookingsRef,
      where("status", "in", ["approved", "reserved"])
    );

    return onSnapshot(
      bookingsQuery,
      (snapshot) => {
        setBookings(
          snapshot.docs.map((bookingDoc) => ({
            id: bookingDoc.id,
            ...bookingDoc.data(),
          }))
        );
      },
      (error) => {
        console.error("Fel vid hämtning av bokningar till löneexport:", error);
        setBookings([]);
      }
    );
  }, [companyRootUid]);

  useEffect(() => {
    if (!companyRootUid) {
      setObRules([]);
      return;
    }

    const obRulesRef = collection(db, "users", companyRootUid, "obRules");
    const q = query(obRulesRef, orderBy("createdAt", "desc"));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const rules = [];

        snapshot.forEach((docItem) => {
          rules.push({
            id: docItem.id,
            ...docItem.data(),
          });
        });

        setObRules(rules);
      },
      (error) => {
        console.error("Fel vid hämtning av OB-regler:", error);
        setObRules([]);
      }
    );

    return () => unsubscribe();
  }, [companyRootUid]);

  useEffect(() => {
    if (!companyRootUid) {
      setHolidays([]);
      return;
    }

    const holidaysRef = collection(db, "users", companyRootUid, "holidays");
    const q = query(holidaysRef, orderBy("date", "asc"));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const list = [];

        snapshot.forEach((docItem) => {
          list.push({
            id: docItem.id,
            ...docItem.data(),
          });
        });

        setHolidays(list);
      },
      (error) => {
        console.error("Fel vid hämtning av helgdagar:", error);
        setHolidays([]);
      }
    );

    return () => unsubscribe();
  }, [companyRootUid]);

  useEffect(() => {
    if (!companyRootUid) {
      setOvertimeRules([]);
      return;
    }

    const overtimeRulesRef = collection(db, "users", companyRootUid, "overtimeRules");

    const unsubscribe = onSnapshot(
      overtimeRulesRef,
      (snapshot) => {
        const rules = snapshot.docs.map((docItem) => ({
          id: docItem.id,
          ...docItem.data(),
        }));

        setOvertimeRules(rules);
      },
      (error) => {
        console.error("Fel vid hämtning av övertidsregler:", error);
        setOvertimeRules([]);
      }
    );

    return () => unsubscribe();
  }, [companyRootUid]);

  useEffect(() => {
    if (!companyRootUid) {
      setApprovedOvertimeRequests([]);
      return;
    }

    const overtimeRequestsRef = collection(
      db,
      "users",
      companyRootUid,
      "overtimeRequests"
    );

    const q = query(overtimeRequestsRef, where("status", "==", "approved"));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const requests = snapshot.docs.map((docItem) => ({
          id: docItem.id,
          ...docItem.data(),
        }));

        setApprovedOvertimeRequests(requests);
      },
      (error) => {
        console.error("Fel vid hämtning av godkända övertidsansökningar:", error);
        setApprovedOvertimeRequests([]);
      }
    );

    return () => unsubscribe();
  }, [companyRootUid]);

  useEffect(() => {
    if (!companyRootUid) {
      setAbsenceTypes([]);
      return;
    }

    const absenceTypesRef = collection(db, "users", companyRootUid, "absenceTypes");

    const unsubscribe = onSnapshot(
      absenceTypesRef,
      (snapshot) => {
        const types = snapshot.docs.map((docItem) => ({
          id: docItem.id,
          ...docItem.data(),
        }));

        types.sort((a, b) => (a.name || "").localeCompare(b.name || "", "sv-SE"));
        setAbsenceTypes(types);
      },
      (error) => {
        console.error("Fel vid hämtning av frånvarotyper:", error);
        setAbsenceTypes([]);
      }
    );

    return () => unsubscribe();
  }, [companyRootUid]);

  useEffect(() => {
    if (!companyRootUid) {
      setApprovedAbsenceRequests([]);
      return;
    }

    const absenceRequestsRef = collection(
      db,
      "users",
      companyRootUid,
      "absenceRequests"
    );

    const q = query(absenceRequestsRef, where("status", "==", "approved"));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const requests = snapshot.docs.map((docItem) => ({
          id: docItem.id,
          ...docItem.data(),
        }));

        setApprovedAbsenceRequests(requests);
      },
      (error) => {
        console.error("Fel vid hämtning av godkända frånvaroansökningar:", error);
        setApprovedAbsenceRequests([]);
      }
    );

    return () => unsubscribe();
  }, [companyRootUid]);

  // ===== BERÄKNINGAR =====

  const employeeMap = useMemo(() => {
    const map = {};
    employees.forEach((employee) => {
      map[employee.id] = employee;
    });
    return map;
  }, [employees]);

  const buildEntries = (shift) => {
    const entries = [];
    const addedEmployeeIds = new Set();

    const addEntry = (employeeId, employeeData = {}) => {
      const normalizedEmployeeId = employeeId || null;
      const isObemannad = Boolean(employeeData?.isObemannad);

      if (!normalizedEmployeeId || isObemannad) return;
      if (addedEmployeeIds.has(normalizedEmployeeId)) return;

      addedEmployeeIds.add(normalizedEmployeeId);

      const baseEmployeeData = {
        breaks: employeeData?.breaks || [],
        overtime: employeeData?.overtime || [],
        absences: employeeData?.absences || [],
        ...employeeData,
      };

      const employeeDataWithApprovedOvertime = mergeApprovedOvertimeForEmployee({
        shift,
        employeeId: normalizedEmployeeId,
        employeeData: baseEmployeeData,
        approvedOvertimeRequests,
        employees,
        overtimeRules,
      });

      const employeeDataWithApprovedRequests = mergeApprovedAbsenceForEmployee({
        shift,
        employeeId: normalizedEmployeeId,
        employeeData: employeeDataWithApprovedOvertime,
        approvedAbsenceRequests,
        employees,
        absenceTypes,
      });

      entries.push({
        employeeId: normalizedEmployeeId,
        employeeData: employeeDataWithApprovedRequests,
      });
    };

    if (Array.isArray(shift?.employees) && shift.employees.length > 0) {
      shift.employees.forEach((employeeData) => {
        addEntry(employeeData?.employeeId, employeeData);
      });
    } else {
      addEntry(shift?.employeeId, {
        payrollTime: shift?.payrollTime || null,
      breaks: shift?.breaks || [],
        overtime: shift?.overtime || [],
        absences: shift?.absences || [],
        employeeId: shift?.employeeId || null,
        isObemannad: shift?.isObemannad || false,
        name: shift?.name || shift?.employee || shift?.userName || "",
      });
    }

    const shiftIdentifier = shift?.originalId || shift?.id;
    bookings
      .filter((booking) => {
        const bookingShiftId = booking?.originalShiftId || booking?.shiftId;
        return (
          ["approved", "reserved"].includes(booking?.status) &&
          booking?.userId &&
          bookingShiftId &&
          String(bookingShiftId) === String(shiftIdentifier)
        );
      })
      .forEach((booking) => {
        addEntry(booking.userId, {
          employeeId: booking.userId,
          name: booking.userName || "",
          breaks: [],
          overtime: [],
          absences: [],
          bookingId: booking.id,
        });
      });

    return entries;
  };

  // ===== 1. TOTALT TIMMAR =====
  const workedHoursByEmployee = useMemo(() => {
    if (!selectedMonth || !selectedYear) return {};

    const selectedMonthIndex = getMonthIndexFromSwedish(selectedMonth);
    const selectedYearNumber = Number(selectedYear);
    const totals = {};

    shifts.forEach((shift) => {
      const shiftDate = safeParseDate(shift.date);
      if (!shiftDate) return;

      if (
        shiftDate.getMonth() !== selectedMonthIndex ||
        shiftDate.getFullYear() !== selectedYearNumber
      ) {
        return;
      }

      const employeeEntries = buildEntries(shift);

      employeeEntries.forEach(({ employeeId, employeeData }) => {
        const ordinaryHours = calculateShiftHours(shift, employeeData);
        const overtime = calculateOvertimeForShift(shift, employeeData);
        const hours = ordinaryHours + overtime.hours;

        totals[employeeId] = (totals[employeeId] || 0) + hours;
      });
    });

    return totals;
  }, [
    shifts,
    bookings,
    selectedMonth,
    selectedYear,
    approvedOvertimeRequests,
    overtimeRules,
    approvedAbsenceRequests,
    employees,
    absenceTypes,
  ]);

  // ===== 2. ÖVERTID TIMMAR =====
  const overtimeByEmployee = useMemo(() => {
    if (!selectedMonth || !selectedYear) return {};

    const selectedMonthIndex = getMonthIndexFromSwedish(selectedMonth);
    const selectedYearNumber = Number(selectedYear);
    const totals = {};

    shifts.forEach((shift) => {
      const shiftDate = safeParseDate(shift.date);
      if (!shiftDate) return;

      if (
        shiftDate.getMonth() !== selectedMonthIndex ||
        shiftDate.getFullYear() !== selectedYearNumber
      ) {
        return;
      }

      const employeeEntries = buildEntries(shift);

      employeeEntries.forEach(({ employeeId, employeeData }) => {
        const overtime = calculateOvertimeForShift(shift, employeeData);

        totals[employeeId] = totals[employeeId] || {
          hours: 0,
          premiumValue: 0,
          hoursByCode: {},
        };

        totals[employeeId].hours += overtime.hours;
        totals[employeeId].premiumValue += overtime.premiumValue;
        Object.entries(overtime.hoursByCode || {}).forEach(([code, hours]) => {
          totals[employeeId].hoursByCode[code] =
            (totals[employeeId].hoursByCode[code] || 0) + Number(hours || 0);
        });
      });
    });

    return totals;
  }, [
    shifts,
    bookings,
    selectedMonth,
    selectedYear,
    approvedOvertimeRequests,
    overtimeRules,
    approvedAbsenceRequests,
    employees,
    absenceTypes,
  ]);

  // ===== 3. FRÅNVARO TIMMAR =====
  const absenceByEmployee = useMemo(() => {
    if (!selectedMonth || !selectedYear) return {};

    const selectedMonthIndex = getMonthIndexFromSwedish(selectedMonth);
    const selectedYearNumber = Number(selectedYear);
    const totals = {};

    shifts.forEach((shift) => {
      const shiftDate = safeParseDate(shift.date);
      if (!shiftDate) return;

      if (
        shiftDate.getMonth() !== selectedMonthIndex ||
        shiftDate.getFullYear() !== selectedYearNumber
      ) {
        return;
      }

      const employeeEntries = buildEntries(shift);

      employeeEntries.forEach(({ employeeId, employeeData }) => {
        const absence = calculateAbsenceForShift(shift, employeeData);

        totals[employeeId] = totals[employeeId] || {
          hours: 0,
          value: 0,
        };

        totals[employeeId].hours += absence.hours;
        totals[employeeId].value += absence.value;
      });
    });

    return totals;
  }, [
    shifts,
    bookings,
    selectedMonth,
    selectedYear,
    approvedOvertimeRequests,
    overtimeRules,
    approvedAbsenceRequests,
    employees,
    absenceTypes,
  ]);

  // ===== 4. OB-PENGAR (UPPDATERAD med ny beräkning) =====
  const obTotalsByEmployee = useMemo(() => {
    if (!selectedMonth || !selectedYear) return {};

    const selectedMonthIndex = getMonthIndexFromSwedish(selectedMonth);
    const selectedYearNumber = Number(selectedYear);
    const totals = {};

    shifts.forEach((shift) => {
      const shiftDate = safeParseDate(shift.date);
      if (!shiftDate) return;

      if (
        shiftDate.getMonth() !== selectedMonthIndex ||
        shiftDate.getFullYear() !== selectedYearNumber
      ) {
        return;
      }

      const employeeEntries = buildEntries(shift);

      employeeEntries.forEach(({ employeeId, employeeData }) => {
        const employee = employeeMap[employeeId];
        if (!employee) return;

        const employeeCustomObEntries = customObEntries.filter(
          entry => entry.employeeId === employeeId
        );

        const obResult = calculateObForShift(
          shift,
          obRules,
          holidays,
          employee.obEnabled,
          employeeData,
          employeeCustomObEntries
        );

        totals[employeeId] = totals[employeeId] || {
          hours: 0,
          value: 0,
          hoursByCode: {},
          valueByCode: {},
        };

        totals[employeeId].hours += Number(obResult.hours || 0);
        totals[employeeId].value += Number(obResult.value || 0);
        Object.entries(obResult.hoursByCode || {}).forEach(([code, hours]) => {
          totals[employeeId].hoursByCode[code] =
            (totals[employeeId].hoursByCode[code] || 0) + Number(hours || 0);
        });
        Object.entries(obResult.valueByCode || {}).forEach(([code, value]) => {
          totals[employeeId].valueByCode[code] =
            (totals[employeeId].valueByCode[code] || 0) + Number(value || 0);
        });
      });
    });

    return totals;
  }, [
    shifts,
    bookings,
    selectedMonth,
    selectedYear,
    obRules,
    holidays,
    employeeMap,
    approvedOvertimeRequests,
    overtimeRules,
    approvedAbsenceRequests,
    employees,
    absenceTypes,
    customObEntries,
  ]);

  // ===== SAMMANSTÄLLNING =====

  const employeesWithPayroll = useMemo(() => {
    return employees
      .map((employee) => {
        const hasSelectedSalary = Boolean(employee.draftSalaryTypeId);
        const workedHours = Number(workedHoursByEmployee[employee.id] ?? 0);
        const overtime = overtimeByEmployee[employee.id] || {
          hours: 0,
          premiumValue: 0,
        };
        const absence = absenceByEmployee[employee.id] || {
          hours: 0,
          value: 0,
        };

        const obValue = hasSelectedSalary
          ? Number(obTotalsByEmployee[employee.id]?.value ?? 0)
          : 0;

        const payType = employee.draftPayType || employee.savedPayType || "hourly";
        const payAmount = Number(employee.draftPayAmount || employee.savedPayAmount || 0);
        const semProcent = Number(employee.semProcent || employee.savedSemProcent || 100);
        const semEnabled = Boolean(employee.semEnabled ?? employee.savedSemEnabled ?? true);

        const absencePay = calculateAbsencePay(absence, payType, payAmount, employee);

        // Grundlön
        let basePay = 0;
        if (!hasSelectedSalary) {
          basePay = 0;
        } else if (payType === "monthly") {
          basePay = payAmount;
        } else {
          // Alla faktiskt arbetade timmar får grundlön. Övertidstillägget
          // läggs separat nedan och grundtimmen räknas därför exakt en gång.
          basePay = payAmount * (Number(workedHours || 0) + absencePay.paidHours);
        }

        // Övertidspengar
        const overtimeValue = hasSelectedSalary ? overtimePay(overtime, payType, payAmount, employee) : 0;

        // Månadslön innehåller frånvarotiden och behöver därför avdrag.
        const absenceValue = hasSelectedSalary ? absencePay.deduction : 0;

        // Semesterersättning
        let vacationPay = 0;
        if (hasSelectedSalary && semEnabled) {
          const ordinaryVacationPay = Number(basePay || 0) * (VACATION_PAY_PERCENT / 100);
          vacationPay = ordinaryVacationPay * (Number(semProcent || 100) / 100);
        }

        // TOTAL LÖN (brutto)
        const grossSalary =
          Number(basePay || 0) +
          Number(obValue || 0) +
          Number(overtimeValue || 0) +
          Number(vacationPay || 0) -
          Number(absenceValue || 0);

        const hasPayroll =
          grossSalary > 0 ||
          workedHours > 0 ||
          overtime.hours > 0 ||
          absence.hours > 0 ||
          obValue > 0;

        return {
          ...employee,
          workedHours,
          overtimeHours: overtime.hours,
          absenceHours: absence.hours,
          overtimeValue,
          obValue,
          vacationPay,
          basePay,
          absenceValue,
          payType,
          payAmount,
          totalGross: grossSalary,
          hasPayroll,
        };
      })
      .filter((employee) => employee.hasPayroll);
  }, [
    employees,
    workedHoursByEmployee,
    overtimeByEmployee,
    absenceByEmployee,
    obTotalsByEmployee,
  ]);

  const employeesMatchingFilters = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();

    return employeesWithPayroll.filter((employee) => {
      const matchesSearch =
        !q ||
        employee.name.toLowerCase().includes(q) ||
        employee.email.toLowerCase().includes(q);

      const matchesGroup =
        !selectedGroupId ||
        (selectedGroupId === "__without_group" ? !employee.groupId : employee.groupId === selectedGroupId);

      return matchesSearch && matchesGroup;
    });
  }, [employeesWithPayroll, searchQuery, selectedGroupId]);

  const approvalPeriod = selectedMonth && selectedYear
    ? `${selectedYear}-${pad(getMonthIndexFromSwedish(selectedMonth) + 1)}`
    : "";
  const approvalsReady = payrollApprovals.ready &&
    payrollApprovals.companyRootUid === companyRootUid &&
    payrollApprovals.period === approvalPeriod;
  const approvalRecords = approvalsReady ? payrollApprovals.records : new Map();
  const approvedIds = new Set(approvalRecords.keys());
  const filteredEmployees = employeesMatchingFilters.filter((employee) =>
    !approvalsReady || selectedApprovalStatus === "all" ||
    (selectedApprovalStatus === "approved") === approvedIds.has(employee.id)
  );
  const allVisibleApproved = filteredEmployees.length > 0 &&
    filteredEmployees.every((employee) => approvedIds.has(employee.id));
  const someVisibleApproved = filteredEmployees.some((employee) => approvedIds.has(employee.id));
  const selectedPayrollEmployee = employeesWithPayroll.find(
    (employee) => employee.id === selectedPayrollEmployeeId
  );

  useEffect(() => {
    if (!companyRootUid || !approvalPeriod) return;

    setApprovalError("");
    setPayrollApprovals({ companyRootUid, period: approvalPeriod, records: new Map(), ready: false });
    const approvalsRef = collection(db, "users", companyRootUid, "payrollApprovals");
    const approvalsQuery = query(approvalsRef, where("period", "==", approvalPeriod));
    const unsubscribe = onSnapshot(
      approvalsQuery,
      (snapshot) => {
        setPayrollApprovals({
          companyRootUid,
          period: approvalPeriod,
          records: new Map(snapshot.docs
            .filter((entry) => entry.data().approved === true && entry.data().employeeId)
            .map((entry) => [entry.data().employeeId, entry.data()])),
          ready: true,
        });
      },
      (error) => {
        console.error("Fel vid hämtning av löne­status:", error);
        setApprovalError("Kunde inte läsa lönestatus. Försök ladda om sidan.");
        setPayrollApprovals({ companyRootUid, period: approvalPeriod, records: new Map(), ready: false });
      }
    );
    return () => unsubscribe();
  }, [companyRootUid, approvalPeriod]);

  const setApproval = async (employeeIds, approved) => {
    if (!companyRootUid || !approvalsReady || approvalSaving || employeeIds.length === 0) return;
    const approverEmployee = employees.find((employee) =>
      employee.id === currentUser?.uid ||
      (currentUser?.email && employee.email?.trim().toLowerCase() === currentUser.email.trim().toLowerCase())
    );
    const profileData = profile?.employedData || profile?.userData || profile;
    const approverName = approverEmployee?.name ||
      [profileData?.firstName, profileData?.lastName].filter(Boolean).join(" ").trim() ||
      profileData?.name || currentUser?.displayName || currentUser?.email || "Okänd användare";
    setApprovalSaving(true);
    setApprovalError("");
    try {
      // Firestore batches support at most 500 writes.
      for (let offset = 0; offset < employeeIds.length; offset += 500) {
        const batch = writeBatch(db);
        employeeIds.slice(offset, offset + 500).forEach((employeeId) => {
          const approvalRef = doc(db, "users", companyRootUid, "payrollApprovals", `${approvalPeriod}_${employeeId}`);
          if (approved) {
            const employee = employeesWithPayroll.find((item) => item.id === employeeId);
            batch.set(approvalRef, {
              period: approvalPeriod,
              employeeId,
              approved: true,
              totalGross: Number(employee?.totalGross || 0),
              approvedByUid: currentUser?.uid || "",
              approvedByName: approverName,
              approvedAt: serverTimestamp(),
            });
          } else {
            batch.delete(approvalRef);
          }
        });
        await batch.commit();
      }
    } catch (error) {
      console.error("Fel vid sparning av lönestatus:", error);
      setApprovalError("Kunde inte spara lönestatus. Försök igen.");
    } finally {
      setApprovalSaving(false);
    }
  };

  const summary = useMemo(
    () => ({
      employeeCount: filteredEmployees.length,
      totalWorkedHours: filteredEmployees.reduce(
        (sum, employee) => sum + Number(employee.workedHours || 0),
        0
      ),
      totalGross: filteredEmployees.reduce(
        (sum, employee) => sum + Number(employee.totalGross || 0),
        0
      ),
      totalNet: filteredEmployees.reduce(
        (sum, employee) => sum + calculateNetSalary(employee.totalGross, employee.tax),
        0
      ),
    }),
    [filteredEmployees]
  );

  // ===== EXPORT =====

  const escapeXml = (value) => String(value ?? "")
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&apos;");

  const getExportPeriod = () => {
    const month = getMonthIndexFromSwedish(selectedMonth);
    const year = Number(selectedYear);
    const pad = (value) => String(value).padStart(2, "0");
    return {
      from: `${year}-${pad(month + 1)}-01`,
      to: `${year}-${pad(month + 1)}-${pad(new Date(year, month + 1, 0).getDate())}`,
      suffix: `${year}-${pad(month + 1)}`,
    };
  };

  const getExportableEmployees = () => {
    const missing = filteredEmployees.filter((employee) => {
      const number = String(employee.employmentNumber || "").trim();
      return !number || number === "–" || number === "-";
    });
    if (missing.length) {
      alert(`Anställningsnummer saknas för: ${missing.map((employee) => employee.name).join(", ")}. Lägg till numret innan export.`);
      return null;
    }
    return filteredEmployees;
  };

  const downloadLatin1Xml = (content, filename) => {
    const bytes = Uint8Array.from(content, (character) => {
      const code = character.charCodeAt(0);
      return code <= 255 ? code : 63;
    });
    const url = URL.createObjectURL(new Blob([bytes], { type: "application/xml;charset=iso-8859-1" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const handleExportWithSystem = (selectedSystem) => {
    if (selectedSystem === "fortnox") {
      exportToFortnox();
    } else if (selectedSystem === "visma") {
      exportToVisma();
    }

    setShowExportPopup(false);
  };

  const openExportPopup = () => {
    const currentDate = new Date();
    setSelectedMonth(monthsInSwedish[currentDate.getMonth()]);
    setSelectedYear(currentDate.getFullYear().toString());
    setShowExportPopup(true);
  };

  const exportToFortnox = () => {
    const exportEmployees = getExportableEmployees();
    if (!exportEmployees) return;
    const { suffix } = getExportPeriod();
    const selectedMonthIndex = getMonthIndexFromSwedish(selectedMonth);
    const selectedYearNumber = Number(selectedYear);
    const exportEmployeeMap = Object.fromEntries(
      exportEmployees.map((employee) => [employee.id, employee])
    );
    const transactionTotals = {};

    const addTotal = (employee, code, date, hours) => {
      const numericHours = Number(hours || 0);
      if (!employee || !date || numericHours <= 0) return;
      const key = `${employee.id}|${code}|${date}`;
      transactionTotals[key] = transactionTotals[key] || {
        employee,
        code,
        date,
        hours: 0,
      };
      transactionTotals[key].hours += numericHours;
    };

    shifts.forEach((shift) => {
      const shiftDate = safeParseDate(shift.date);
      if (
        !shiftDate ||
        shiftDate.getMonth() !== selectedMonthIndex ||
        shiftDate.getFullYear() !== selectedYearNumber
      ) return;

      const shiftDateKey = formatDateKey(shiftDate);

      buildEntries(shift).forEach(({ employeeId, employeeData }) => {
        const employee = exportEmployeeMap[employeeId];
        if (!employee) return;

        const payType = employee.draftPayType || employee.savedPayType || "hourly";
        const overtime = calculateOvertimeForShift(shift, employeeData);
        if (payType === "hourly") {
          addTotal(
            employee,
            "ARB",
            shiftDateKey,
            calculateShiftHours(shift, employeeData) + overtime.hours
          );
        }

        Object.entries(overtime.hoursByCode || {}).forEach(([code, hours]) => {
          addTotal(employee, code, shiftDateKey, hours);
        });

        (employeeData.absences || []).forEach((absence) => {
          addTotal(
            employee,
            getFortnoxAbsenceCode(absence),
            shiftDateKey,
            calculateEntryHours(shift, absence, employeeData)
          );
        });

        const employeeCustomObEntries = customObEntries.filter(
          (entry) => entry.employeeId === employeeId
        );
        const obResult = calculateObForShift(
          shift,
          obRules,
          holidays,
          employee.obEnabled,
          employeeData,
          employeeCustomObEntries
        );
        Object.entries(obResult.hoursByDateAndCode || {}).forEach(
          ([date, codeTotals]) => {
            Object.entries(codeTotals).forEach(([code, hours]) => {
              addTotal(employee, code, date, hours);
            });
          }
        );
      });
    });

    let postId = 1;
    const transactions = Object.values(transactionTotals)
      .sort((a, b) => a.date.localeCompare(b.date) || a.employee.name.localeCompare(b.employee.name, "sv-SE") || a.code.localeCompare(b.code, "sv-SE"))
      .map(({ employee, code, date, hours }) =>
        `<tidtrans postid="${postId++}" anstid="${escapeXml(employee.employmentNumber)}"><tidkod>${escapeXml(code)}</tidkod><datum>${date}</datum><timmar>${hours.toFixed(2)}</timmar></tidtrans>`
      );
    const xml = `<?xml version="1.0" encoding="iso-8859-1"?>\n<paxml xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xsi:noNamespaceSchemaLocation="http://www.paxml.se/2.0/paxml.xsd"><header><version>2.0</version><format>LÖNIN</format><datum>${new Date().toISOString()}</datum><foretagnamn>${escapeXml(activeCompany?.companyName || "")}</foretagnamn><programnamn>Schema</programnamn></header><tidtransaktioner>${transactions.join("")}</tidtransaktioner></paxml>`;
    downloadLatin1Xml(xml, `fortnox-loneunderlag-${suffix}.pax`);
  };

  const exportToVisma = () => {
    const exportEmployees = getExportableEmployees();
    if (!exportEmployees) return;
    const { from, to, suffix } = getExportPeriod();
    const codes = [["ARBETE", "Arbetad tid"], ["OVERTID", "Övertid"], ["FRANVARO", "Frånvaro"], ["OB", "OB-tid"]];
    const employeesXml = exportEmployees.map((employee) => {
      const parts = String(employee.name || "Användare").trim().split(/\s+/);
      const firstName = employee.firstName || parts.shift() || "Användare";
      const lastName = employee.lastName || parts.join(" ") || "-";
      const times = [];
      const add = (code, hours) => {
        if (Number(hours) > 0) times.push(`<Time DateOfReport='${to}' TimeCode='${code}' SumOfHours='${Number(hours).toFixed(2)}'/>`);
      };
      add("ARBETE", employee.workedHours);
      add("OVERTID", employee.overtimeHours);
      add("FRANVARO", employee.absenceHours);
      add("OB", obTotalsByEmployee[employee.id]?.hours);
      return `<SalaryDataEmployee FromDate='${from}' ToDate='${to}'><Employee EmploymentNo='${escapeXml(employee.employmentNumber)}' FirstName='${escapeXml(firstName)}' Name='${escapeXml(lastName)}' FromDate='${from}' ToDate='${to}'><NormalWorkingTimes><NormalWorkingTime DateOfReport='${to}' NormalWorkingTimeHours='0.00'/></NormalWorkingTimes><Times>${times.join("")}</Times><TimeAdjustments></TimeAdjustments><TimeBalances></TimeBalances><RegOutlays></RegOutlays></Employee></SalaryDataEmployee>`;
    }).join("");
    const xml = `<?xml version="1.0" encoding="ISO-8859-1"?>\n<SalaryData ProgramName='Schema' ExportVersion='1.3' Created='${new Date().toISOString().slice(0, 10)}' Type='SalaryData' Language='Swedish' CompanyName='${escapeXml(activeCompany?.companyName || "")}'><TimeCodes>${codes.map(([code, name]) => `<TimeCode Code='${code}' TimeCodeName='${name}'/>`).join("")}</TimeCodes>${employeesXml}</SalaryData>`;
    downloadLatin1Xml(xml, `visma-loneunderlag-${suffix}.tlu`);
  };

  // ===== RENDER =====

  if (companyLoading || loading || !selectedMonth || !selectedYear) {
    return <LoadingIndicator message="Laddar löneunderlag..." fullPage />;
  }

  if (!activeCompany && !companyRootUid) {
    return (
      <div style={styles.page}>
        <div style={styles.container}>
          <div style={styles.emptyState}>
            <p>Inget företag valt. Vänligen logga in igen.</p>
            <button
              onClick={() => {
                window.location.href = "/login";
              }}
              style={{
                padding: "10px 20px",
                background: "#6323ef",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                cursor: "pointer",
                marginTop: 16,
              }}
            >
              Gå till inloggning
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (selectedPayrollEmployee) {
    return (
      <TimeReportPopup
        key={`${companyRootUid}_${selectedPayrollEmployee.id}`}
        isOpen
        employee={selectedPayrollEmployee}
        companyRootUid={companyRootUid}
        initialMonth={new Date(Number(selectedYear), getMonthIndexFromSwedish(selectedMonth), 1)}
        backLabel="Tillbaka till löneexport"
        onClose={() => setSelectedPayrollEmployeeId(null)}
      />
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <div style={{ marginBottom: 16 }}>
          <p style={{ color: "#6b7280", fontSize: 14, margin: 0 }}>
            Visar löneunderlag för:{" "}
            <strong>{activeCompany?.companyName || "företaget"}</strong>
          </p>
        </div>

        <div style={styles.topRow}>
          <div>
            <h1 style={styles.title}>Löneexport & sammanställning</h1>
            <p style={styles.subtitle}>
              Översikt över arbetade timmar, övertid, frånvaro, OB och lön för vald period.
            </p>
          </div>

          <button
            style={styles.exportButton}
            onClick={openExportPopup}
          >
            <FaFileExport size={20} />
            Exportera löneunderlag
          </button>
        </div>

        <div style={styles.divider} />

        <div style={styles.summaryPanel}>
          <h2 style={styles.summaryTitle}>
            SAMMANFATTNING – {selectedMonth.toUpperCase()} {selectedYear}
          </h2>

          <div style={styles.summaryMeta}>
            ARBETAD PERIOD: {selectedMonth.toUpperCase()} {selectedYear} ·
            LÖNEPERIOD (UTBETALNING):{" "}
            {monthsInSwedish[
              (getMonthIndexFromSwedish(selectedMonth) + 1) % 12
            ].toUpperCase()}{" "}
            {getMonthIndexFromSwedish(selectedMonth) === 11
              ? Number(selectedYear) + 1
              : selectedYear}
          </div>

          <div style={styles.summaryGrid}>
            <div style={styles.summaryCard}>
              <div style={styles.summaryCardLabel}>Antal anställda</div>
              <div style={styles.summaryCardValue}>{summary.employeeCount}</div>
            </div>

            <div style={styles.summaryCard}>
              <div style={styles.summaryCardLabel}>Totalt arbetade timmar</div>
              <div style={styles.summaryCardValue}>
                {formatHours(summary.totalWorkedHours)} h
              </div>
            </div>

            <div style={styles.summaryCard}>
              <div style={styles.summaryCardLabel}>Total lön (brutto)</div>
              <div style={styles.summaryCardValue}>
                {formatCurrency(summary.totalGross)}
              </div>
            </div>

            <div style={styles.summaryCard}>
              <div style={styles.summaryCardLabel}>Total lön (netto)</div>
              <div style={styles.summaryCardValue}>
                {formatCurrency(summary.totalNet)}
              </div>
            </div>
          </div>
        </div>

        <div style={styles.filtersWrap}>
          <div style={styles.searchWrap}>
            <FaSearch style={styles.searchIcon} />

            <input
              type="text"
              placeholder="Sök efter namn, email..."
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              style={styles.searchInput}
            />
          </div>

          <select
            aria-label="Filtrera på grupp"
            value={selectedGroupId}
            onChange={(event) => setSelectedGroupId(event.target.value)}
            style={styles.select}
          >
            <option value="">Alla grupper</option>
            {employeeGroups.map((group) => (
              <option key={group.id} value={group.id}>
                {group.name}
              </option>
            ))}
            <option value="__without_group">Utan grupp</option>
          </select>

          <select
            aria-label="Visa lönestatus"
            value={selectedApprovalStatus}
            onChange={(event) => setSelectedApprovalStatus(event.target.value)}
            style={styles.select}
          >
            <option value="all">Alla statusar</option>
            <option value="approved">Godkända</option>
            <option value="unapproved">Ej godkända</option>
          </select>

          <select
            value={selectedMonth}
            onChange={(event) => setSelectedMonth(event.target.value)}
            style={styles.select}
          >
            {monthsInSwedish.map((month) => (
              <option key={month} value={month}>
                {month}
              </option>
            ))}
          </select>

          <select
            value={selectedYear}
            onChange={(event) => setSelectedYear(event.target.value)}
            style={styles.select}
          >
            {years.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>

        {approvalError && <p role="alert" style={{ color: "#b42318", margin: "0 0 12px" }}>{approvalError}</p>}

        {filteredEmployees.length === 0 ? (
          <div style={styles.emptyState}>
            {employees.length === 0
              ? "Inga anställda hittades för detta företag."
              : "Ingen data hittades för valt filter."}
          </div>
        ) : (
          <div style={styles.tableWrap}>
            <div style={{ overflowX: "auto" }}>
              <table style={styles.table}>
                <thead style={styles.thead}>
                  <tr>
                    <th scope="col" style={{ ...styles.th, background: "#f1f7ff", textAlign: "center" }}>
                      <input
                        type="checkbox"
                        aria-label="Godkänn alla visade löneunderlag"
                        checked={allVisibleApproved}
                        ref={(input) => { if (input) input.indeterminate = someVisibleApproved && !allVisibleApproved; }}
                        disabled={!approvalsReady || approvalSaving}
                        onChange={(event) => setApproval(
                          filteredEmployees.map((employee) => employee.id), event.target.checked
                        )}
                        style={{ width: 20, height: 20, cursor: "pointer" }}
                      />
                    </th>
                    <th scope="col" style={{ ...styles.th, background: "#f1f7ff" }}>Lönestatus</th>
                    <th style={styles.th}>Namn</th>
                    <th style={styles.th}>Personnummer</th>
                    <th style={styles.th}>Anställningsnr</th>
                    <th style={styles.th}>Grupp</th>
                    <th style={{ ...styles.th, textAlign: "right" }}>Bruttolön</th>
                    <th style={{ ...styles.th, textAlign: "right" }}>Nettolön</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredEmployees.map((employee) => (
                    <tr
                      key={employee.id}
                      tabIndex={0}
                      aria-label={`Visa löneuppgifter för ${employee.name}`}
                      onClick={() => setSelectedPayrollEmployeeId(employee.id)}
                      onKeyDown={(event) => {
                        if (event.target === event.currentTarget && (event.key === "Enter" || event.key === " ")) {
                          event.preventDefault();
                          setSelectedPayrollEmployeeId(employee.id);
                        }
                      }}
                      style={{ cursor: "pointer" }}
                    >
                      <td style={{ ...styles.td, textAlign: "center" }}>
                        <input
                          type="checkbox"
                          aria-label={`Godkänn löneunderlag för ${employee.name}`}
                          checked={approvedIds.has(employee.id)}
                          disabled={!approvalsReady || approvalSaving}
                          onClick={(event) => event.stopPropagation()}
                          onChange={(event) => setApproval([employee.id], event.target.checked)}
                          style={{ width: 20, height: 20, cursor: "pointer" }}
                        />
                      </td>
                      <td style={styles.td}>
                        <span style={{
                          display: "inline-block",
                          padding: "5px 12px",
                          borderRadius: 999,
                          whiteSpace: "nowrap",
                          fontWeight: 700,
                          color: approvedIds.has(employee.id) ? "#17633a" : "#596579",
                          background: approvedIds.has(employee.id) ? "#d6f7df" : "#edf0f5",
                        }}>
                          {approvalsReady ? (approvedIds.has(employee.id) ? "Godkänd" : "Ej godkänd") : "Laddar…"}
                        </span>
                      </td>
                      <td style={{ ...styles.td, ...styles.nameCell }}>
                        {employee.name}
                      </td>
                      <td style={styles.td}>{employee.personalNumber || "–"}</td>
                      <td style={styles.td}>{employee.employmentNumber || "-"}</td>
                      <td style={styles.td}>{employeeGroups.find((group) => group.id === employee.groupId)?.name || "–"}</td>
                      <td style={{ ...styles.td, ...styles.totalSalaryCell, textAlign: "right", whiteSpace: "nowrap" }}>
                        {formatCurrency(employee.totalGross)}
                      </td>
                      <td style={{ ...styles.td, ...styles.totalSalaryCell, textAlign: "right", whiteSpace: "nowrap" }}>
                        {formatCurrency(calculateNetSalary(employee.totalGross, employee.tax))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {showExportPopup && (
        <ExportSystemPopup
          onClose={() => setShowExportPopup(false)}
          onSelect={handleExportWithSystem}
          onPeriodChange={(month, year) => {
            setSelectedMonth(month);
            setSelectedYear(year);
          }}
        />
      )}

    </div>
  );
}
